import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, RotateCcw, Save, Trash2 } from "lucide-react";
import { useAdminEmployeeShifts } from "@/hooks/use-admin-queries";
import { DatePicker } from "@/components/ui/date-picker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { adminApi } from "@/lib/api/admin";
import { getAdminPassword } from "@/lib/auth/storage";
import { todayIST } from "@/lib/timezone";
import type { EmployeeShiftBlock, EmployeeShiftRow } from "@/lib/types";

type DraftBlock = {
  start_time: string;
  end_time: string;
};

function defaultBlocksForRow(row: EmployeeShiftRow): DraftBlock[] {
  if (row.has_custom_shift && row.blocks.length > 0) {
    return row.blocks.map((b) => ({
      start_time: b.start_time,
      end_time: b.end_time,
    }));
  }
  if (row.default_blocks && row.default_blocks.length > 0) {
    return row.default_blocks.map((b) => ({
      start_time: b.start_time,
      end_time: b.end_time,
    }));
  }
  return [
    { start_time: "10:00", end_time: "14:00" },
    { start_time: "17:00", end_time: "21:00" },
  ];
}

function blockLabel(block: DraftBlock) {
  return `${block.start_time}–${block.end_time}`;
}

export default function EmployeeShiftsPage() {
  const [date, setDate] = useState(todayIST());
  const adminPassword = getAdminPassword();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useAdminEmployeeShifts(date);
  const [drafts, setDrafts] = useState<Record<string, DraftBlock[]>>({});

  useEffect(() => {
    if (!data?.rows) return;
    const next: Record<string, DraftBlock[]> = {};
    for (const row of data.rows) {
      next[row.person_id] = defaultBlocksForRow(row);
    }
    setDrafts(next);
  }, [data]);

  const save = useMutation({
    mutationFn: (row: EmployeeShiftRow) =>
      adminApi.upsertEmployeeShift(adminPassword!, row.person_id, {
        date,
        blocks: (drafts[row.person_id] ?? []).map((block, index) => ({
          start_time: block.start_time,
          end_time: block.end_time,
          sort_order: index + 1,
        })),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "employee-shifts", date] });
      queryClient.invalidateQueries({ queryKey: ["admin", "register"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "monthly"] });
    },
  });

  const reset = useMutation({
    mutationFn: (personId: string) =>
      adminApi.deleteEmployeeShift(adminPassword!, personId, date),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "employee-shifts", date] });
      queryClient.invalidateQueries({ queryKey: ["admin", "register"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "monthly"] });
    },
  });

  function updateBlock(
    personId: string,
    blockIndex: number,
    field: keyof DraftBlock,
    value: string
  ) {
    setDrafts((prev) => {
      const blocks = [...(prev[personId] ?? [])];
      blocks[blockIndex] = { ...blocks[blockIndex], [field]: value };
      return { ...prev, [personId]: blocks };
    });
  }

  function addBlock(personId: string) {
    setDrafts((prev) => ({
      ...prev,
      [personId]: [
        ...(prev[personId] ?? []),
        { start_time: "17:00", end_time: "21:00" },
      ],
    }));
  }

  function removeBlock(personId: string, blockIndex: number) {
    setDrafts((prev) => {
      const blocks = [...(prev[personId] ?? [])];
      if (blocks.length <= 1) return prev;
      blocks.splice(blockIndex, 1);
      return { ...prev, [personId]: blocks };
    });
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Employee Shifts</h2>
          <p className="text-sm text-muted-foreground">
            Set one or more shift blocks per employee for each day — e.g. 10:00–14:00
            and 17:00–21:00.
          </p>
        </div>
        <DatePicker value={date} onChange={setDate} />
      </div>

      {isLoading && <p className="text-muted-foreground">Loading shifts…</p>}
      {error && <p className="text-red-600">Could not load employee shifts.</p>}

      {data && (
        <div className="space-y-4">
          {data.rows.map((row) => {
            const blocks = drafts[row.person_id] ?? [];
            return (
              <div
                key={row.person_id}
                className="rounded-lg border bg-white p-4 shadow-sm"
              >
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <h3 className="font-semibold">{row.person_name}</h3>
                    {row.has_custom_shift ? (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {row.blocks.map((b: EmployeeShiftBlock) => (
                          <Badge key={b.sort_order} variant="default">
                            {b.label ?? `${b.start_time}–${b.end_time}`}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        Using category default
                        {row.default_blocks?.length
                          ? `: ${row.default_blocks.map((b) => b.label ?? `${b.start_time}–${b.end_time}`).join(", ")}`
                          : ""}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => addBlock(row.person_id)}
                    >
                      <Plus className="mr-1 h-3.5 w-3.5" />
                      Add block
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!blocks.length || save.isPending}
                      onClick={() => save.mutate(row)}
                    >
                      <Save className="mr-1 h-3.5 w-3.5" />
                      Save
                    </Button>
                    {row.has_custom_shift && (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={reset.isPending}
                        onClick={() => reset.mutate(row.person_id)}
                      >
                        <RotateCcw className="mr-1 h-3.5 w-3.5" />
                        Reset
                      </Button>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  {blocks.map((block, index) => (
                    <div
                      key={`${row.person_id}-${index}`}
                      className="flex flex-wrap items-center gap-2 rounded-md bg-muted/30 p-2"
                    >
                      <span className="w-16 text-xs font-medium text-muted-foreground">
                        Block {index + 1}
                      </span>
                      <Input
                        type="time"
                        className="w-32"
                        value={block.start_time}
                        onChange={(e) =>
                          updateBlock(row.person_id, index, "start_time", e.target.value)
                        }
                      />
                      <span className="text-muted-foreground">to</span>
                      <Input
                        type="time"
                        className="w-32"
                        value={block.end_time}
                        onChange={(e) =>
                          updateBlock(row.person_id, index, "end_time", e.target.value)
                        }
                      />
                      <span className="text-xs text-muted-foreground">
                        {blockLabel(block)}
                      </span>
                      {blocks.length > 1 && (
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="ml-auto h-8 w-8"
                          onClick={() => removeBlock(row.person_id, index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {data.rows.length === 0 && (
            <p className="text-muted-foreground">No active employees found.</p>
          )}
        </div>
      )}
    </div>
  );
}
